// Palancas del mock (`.palancas-wrap`), en su orden: cada una se prende con su casilla y lleva sus
// campos en línea. El cupón, que el mock no tiene, va al final.
import type { ReactNode } from 'react';
import { Controller, useFormContext, useWatch, type FieldErrors } from 'react-hook-form';
import type { CatalogProduct } from '@/api/services/retail-catalog/retail-catalog.client';
import { cn } from '@/lib/cn';
import type { SourceState } from '../api/usePromotionFormSources';
import { describeLevers } from '../lib/leverSummary';
import { LEVER_KEYS, leversError, type SalePromotionFormValues } from '../lib/salePromotionForm';
import {
    ACCESSORY_SUBCATEGORY_OPTIONS,
    COUPON_SCOPE_OPTIONS,
    DISCOUNT_TYPE_OPTIONS,
    GIFT_MODE_OPTIONS,
} from '../lib/salePromotionFormOptions';
import { FieldMessage, INLINE_INPUT, INLINE_SELECT, LiveInline, Unit } from './editorParts';
import { ProductSearchPicker } from './ProductSearchPicker';
import { FieldHelp } from '@/shared/patterns/EditorForm';
import { SALE_PROMOTION_HELP } from '../constants/helpText';

type LeverKey = (typeof LEVER_KEYS)[number];

const CHOOSE_COUNT_ERROR_ID = 'lever-gift-choose-count-error';

/** Campos cuyo error va junto al propio campo y no bajo el renglón. */
const INLINE_ERROR_FIELDS = new Set(['chooseCount']);

/** El primer error de la palanca, para pintarlo bajo su renglón. */
function leverError(errors: FieldErrors<SalePromotionFormValues>, lever: LeverKey) {
    const fields = errors[lever] as Record<string, { message?: string } | undefined> | undefined;
    if (!fields) return undefined;
    return Object.entries(fields).find(
        ([key, field]) => !INLINE_ERROR_FIELDS.has(key) && field?.message,
    )?.[1]?.message;
}

function LeverRow({
    lever,
    name,
    children,
    below,
}: {
    lever: LeverKey;
    name: string;
    /** Los campos en línea, a la derecha del nombre. */
    children: ReactNode;
    /** Lo que no cabe en el renglón (p. ej. la lista de regalos); solo con la palanca prendida. */
    below?: ReactNode;
}) {
    const {
        control,
        register,
        formState: { errors },
    } = useFormContext<SalePromotionFormValues>();
    const enabled = useWatch({ control, name: `${lever}.enabled` });
    const id = `lever-${lever}`;
    return (
        <div data-lever={lever}>
            {/* Arriba y no al centro: si los campos parten en dos renglones, el nombre va con el primero. */}
            <div className="grid grid-cols-[14px_140px_minmax(0,1fr)] items-start gap-2 py-1">
                <input
                    id={id}
                    type="checkbox"
                    className="mt-[6px] size-3.5 cursor-pointer accent-primary"
                    {...register(`${lever}.enabled`)}
                />
                <span className="mt-[5px] flex items-center gap-1">
                    <label
                        htmlFor={id}
                        className={cn(
                            'cursor-pointer whitespace-nowrap text-[11px]',
                            enabled
                                ? 'font-semibold text-foreground'
                                : 'font-medium text-muted-foreground',
                        )}
                    >
                        {name}
                    </label>
                    <FieldHelp label={name} help={SALE_PROMOTION_HELP[lever]} />
                </span>
                <div
                    // Apagada no se enfoca ni se edita, pero conserva lo tecleado por si se vuelve a prender.
                    inert={!enabled}
                    className={cn('flex flex-wrap items-center gap-1.5', !enabled && 'opacity-35')}
                >
                    {children}
                </div>
            </div>
            {enabled && below ? <div className="mb-1 ml-[22px]">{below}</div> : null}
            {enabled ? (
                <div className="ml-[22px]">
                    <FieldMessage message={leverError(errors, lever)} />
                </div>
            ) : null}
        </div>
    );
}

/**
 * Tipo ($ fijo o % off) y valor de un descuento. Lo comparten las palancas que descuentan; los
 * campos extra de cada una (tope, vigencia) van en `children`.
 */
function DiscountValueFields({
    lever,
    label,
    children,
}: {
    lever: 'deviceDiscount' | 'accessoryDiscount' | 'coupon';
    label: string;
    children?: ReactNode;
}) {
    const { control, register } = useFormContext<SalePromotionFormValues>();
    const type = useWatch({ control, name: `${lever}.type` });
    return (
        <>
            <select
                aria-label={`Tipo de ${label}`}
                className={INLINE_SELECT}
                {...register(`${lever}.type`)}
            >
                {DISCOUNT_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </select>
            <input
                aria-label={type === 'PERCENTAGE' ? `Porcentaje de ${label}` : `Monto de ${label}`}
                inputMode="decimal"
                placeholder="valor"
                className={INLINE_INPUT}
                {...register(`${lever}.value`)}
            />
            <Unit>{type === 'PERCENTAGE' ? '% off' : '$ off'}</Unit>
            {children}
        </>
    );
}

/** Tipo, valor y «Tope $» opcional: el mismo control para el equipo y los accesorios. */
function CappedDiscountFields({
    lever,
    label,
}: {
    lever: 'deviceDiscount' | 'accessoryDiscount';
    label: string;
}) {
    const { register } = useFormContext<SalePromotionFormValues>();
    return (
        <DiscountValueFields lever={lever} label={label}>
            <Unit>· tope $</Unit>
            <input
                aria-label={`Tope de ${label}`}
                inputMode="decimal"
                placeholder="opcional"
                className={INLINE_INPUT}
                {...register(`${lever}.maxAmount`)}
            />
        </DiscountValueFields>
    );
}

export function SalePromotionLeversEditor({
    products,
    productsState,
    productNames,
    catalogNotice,
}: {
    products: CatalogProduct[];
    productsState: SourceState;
    productNames: ReadonlyMap<string, string>;
    /** Por qué no se elige del catálogo, como con varias cadenas. */
    catalogNotice?: string;
}) {
    const {
        control,
        register,
        formState: { errors },
    } = useFormContext<SalePromotionFormValues>();
    const giftMode = useWatch({ control, name: 'gift.mode' });
    const chooseCountError = errors.gift?.chooseCount?.message;
    const values = useWatch({ control }) as SalePromotionFormValues;
    const summary = describeLevers(values, productNames);
    const accessories = products.filter((product) => product.category === 'ACCESSORY');

    return (
        <>
            <div
                role="group"
                aria-label="Palancas"
                className="flex flex-col gap-0.5 rounded-md border-l-[3px] border-l-primary bg-muted/30 px-3 py-2"
            >
                <LeverRow lever="airtime" name="Tiempo aire">
                    <input
                        aria-label="Número de recargas"
                        inputMode="numeric"
                        placeholder="N"
                        className={cn(INLINE_INPUT, 'w-12')}
                        {...register('airtime.rechargeCount')}
                    />
                    <Unit>recargas de $</Unit>
                    <input
                        aria-label="Monto de cada recarga"
                        inputMode="decimal"
                        placeholder="0"
                        className={INLINE_INPUT}
                        {...register('airtime.rechargeAmount')}
                    />
                </LeverRow>

                <LeverRow
                    lever="gift"
                    name="Accesorio de regalo"
                    below={
                        giftMode === 'LIST' && catalogNotice ? (
                            <p className="text-[10.5px] text-muted-foreground">{catalogNotice}</p>
                        ) : giftMode === 'LIST' ? (
                            <Controller
                                control={control}
                                name="gift.skus"
                                render={({ field }) => (
                                    <ProductSearchPicker
                                        label="Accesorios de regalo"
                                        products={accessories}
                                        state={productsState}
                                        selected={field.value}
                                        onChange={field.onChange}
                                        placeholder="Busca accesorio por SKU, marca o modelo…"
                                    />
                                )}
                            />
                        ) : (
                            <div className="flex flex-wrap items-center gap-1.5">
                                <select
                                    aria-label="Subcategoría de accesorios"
                                    className={INLINE_SELECT}
                                    {...register('gift.accessorySubcategory')}
                                >
                                    <option value="">Elige una subcategoría</option>
                                    {ACCESSORY_SUBCATEGORY_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                                <Unit>hasta $</Unit>
                                <input
                                    aria-label="Precio máximo del regalo"
                                    inputMode="decimal"
                                    placeholder="precio"
                                    className={INLINE_INPUT}
                                    {...register('gift.maxPrice')}
                                />
                            </div>
                        )
                    }
                >
                    <select
                        aria-label="Cómo se define el regalo"
                        className={INLINE_SELECT}
                        {...register('gift.mode')}
                    >
                        {GIFT_MODE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                    <Unit>elige</Unit>
                    <input
                        aria-label="Cuántos elige el cliente"
                        inputMode="numeric"
                        aria-invalid={chooseCountError ? true : undefined}
                        aria-describedby={chooseCountError ? CHOOSE_COUNT_ERROR_ID : undefined}
                        className={cn(INLINE_INPUT, 'w-12')}
                        {...register('gift.chooseCount')}
                    />
                    {chooseCountError ? (
                        <span
                            id={CHOOSE_COUNT_ERROR_ID}
                            className="basis-full text-[10.5px] text-destructive"
                        >
                            {chooseCountError}
                        </span>
                    ) : null}
                </LeverRow>

                <LeverRow lever="deviceDiscount" name="Descuento del equipo">
                    <CappedDiscountFields lever="deviceDiscount" label="descuento del equipo" />
                </LeverRow>

                <LeverRow lever="accessoryDiscount" name="Descuento accesorios">
                    <CappedDiscountFields
                        lever="accessoryDiscount"
                        label="descuento en accesorios"
                    />
                </LeverRow>

                <LeverRow lever="walletBonus" name="Bono al Wallet Fiado">
                    <input
                        aria-label="Monto del bono al Wallet"
                        inputMode="decimal"
                        placeholder="$"
                        className={INLINE_INPUT}
                        {...register('walletBonus.amount')}
                    />
                    <Unit>$ MXN</Unit>
                </LeverRow>

                <LeverRow
                    lever="coupon"
                    name="Cupón próxima compra"
                    below={
                        <div className="flex flex-wrap items-center gap-1.5">
                            <Unit>vence en</Unit>
                            <input
                                aria-label="Días de vigencia del cupón"
                                inputMode="numeric"
                                className={cn(INLINE_INPUT, 'w-12')}
                                {...register('coupon.validityDays')}
                            />
                            <Unit>días ·</Unit>
                            <select
                                aria-label="Dónde vale el cupón"
                                className={INLINE_SELECT}
                                {...register('coupon.scope')}
                            >
                                {COUPON_SCOPE_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    }
                >
                    <DiscountValueFields lever="coupon" label="cupón" />
                </LeverRow>
            </div>
            <LiveInline>
                <b className="font-semibold leading-snug text-foreground">{summary}</b>
            </LiveInline>
            <FieldMessage message={leversError(errors)} />
            <p className="mt-1.5 text-[10px] leading-snug text-muted-foreground">
                Activa las palancas que aplican al cliente. Puedes combinar varias sobre la misma
                venta. El regalo va a $0 y no cuenta en el tope del 30%.
            </p>
        </>
    );
}
